/*
 * Main-thread-only wiring of the client-log shipper (LainGram): batches the
 * debug ring buffers (this window + MTProto worker + service worker) and POSTs
 * them to the sovereign log server so production debugging does not depend on
 * members copy-pasting devtools output.
 *
 * Same structural rules as mountLogExport: this module is imported ONLY from
 * src/index.ts. It pulls apiManagerProxy / MTProtoMessagePort, which would
 * poison worker graphs with circular imports if logsBuffer (a leaf that lives
 * in every worker) ever reached for it — see the comment in mountLogExport.
 *
 * Enabled iff VITE_CLIENTLOG_URL and VITE_CLIENTLOG_KEY are set at build time.
 * The key is a PUBLIC intake key by design (a browser cannot hold secrets);
 * the server side enforces the caps that actually matter (body size, entry
 * count, field truncation, per-IP rate).
 */

import {getLogEntriesSince, LogEntry, setLogBufferEnabled} from './logsBuffer';
import rootScope from '@lib/rootScope';
import Modes from '@config/modes';
import MTProtoMessagePort from '@lib/mainWorker/mainMessagePort';
import apiManagerProxy from '@lib/apiManagerProxy';

const URL_BASE: string | undefined = import.meta.env.VITE_CLIENTLOG_URL;
const INTAKE_KEY: string | undefined = import.meta.env.VITE_CLIENTLOG_KEY;

const FLUSH_INTERVAL_MS = 15_000;
const ERROR_FAST_FLUSH_MS = 3_000;
const MAX_BATCH = 400; // server cap is 512; leave room for the retry buffer
const RETRY_BUFFER_MAX = 1_500;
const COLLECT_TIMEOUT_MS = 5_000;
// Client-side message bound. The server re-truncates, but keeping the payload
// small here is what makes a 4000-entry ring drain cheap on mobile data.
const MSG_LIMIT = 1500;

const LEVEL_NAMES = ['log', 'error', 'warn', 'log', 'debug'];

function levelName(level: number): string {
  return LEVEL_NAMES[level] || 'log';
}

function renderArg(arg: any): string {
  try {
    if(typeof arg === 'string') return arg;
    if(arg instanceof Error) return `${arg.name}: ${arg.message}`;
    if(typeof arg === 'object' && arg !== null) {
      const json = JSON.stringify(arg);
      if(json !== undefined) return json.length > MSG_LIMIT ? json.slice(0, MSG_LIMIT) + '…' : json;
    }
    return String(arg);
  } catch(err) {
    return String(arg);
  }
}

function renderEntry(entry: LogEntry): {ts: number, level: string, src: string, msg: string} {
  let msg = entry.args.map(renderArg).join(' ');
  if(entry.stack) msg += `\n${entry.stack}`;
  return {
    ts: entry.t,
    level: levelName(entry.level),
    src: `${entry.src}:${entry.prefix || ''}`,
    msg
  };
}

// A stable-per-browser tag so batches correlate across flushes without any
// cookie: random once, persisted next to the app's own locals.
function sessionTag(): string {
  const KEY = 'lgClientLogTag';
  try {
    let tag = localStorage.getItem(KEY);
    if(!tag) {
      tag = Math.random().toString(36).slice(2, 10);
      localStorage.setItem(KEY, tag);
    }
    return tag;
  } catch(err) {
    return 'anon';
  }
}

/** Public: the per-browser tag stamped on every shipped batch (E2E bridge reads it). */
export function getClientLogSessionTag(): string {
  return sessionTag();
}

/** Public: force a flush now (E2E bridge / debugging). Resolves when it settles. */
export function flushClientLogs(): Promise<void> {
  return flush();
}

let userId = 0;
let watermark = Date.now() - 60_000; // catch early-boot errors on the first flush
const retryBuffer: {ts: number, level: string, src: string, msg: string}[] = [];
let shipping = false;

async function collectSince(t: number): Promise<LogEntry[]> {
  const all = getLogEntriesSince(t);
  if(!Modes.noWorker) {
    try {
      const master = MTProtoMessagePort.getMasterInstance();
      const workerLogs = await master?.invoke('getLogsSince', t, undefined, undefined, undefined, COLLECT_TIMEOUT_MS);
      if(workerLogs?.length) all.push(...workerLogs);
    } catch(err) {
      // Worker asleep / not spawned yet: the main buffer still ships.
    }
  }
  if(!Modes.noServiceWorker) {
    try {
      const swLogs = await apiManagerProxy.serviceMessagePort?.invoke('getLogsSince', t, undefined, undefined, undefined, COLLECT_TIMEOUT_MS);
      if(swLogs?.length) all.push(...swLogs);
    } catch(err) {
      // SW unregistered / asleep: fine.
    }
  }
  all.sort((a, b) => a.t - b.t);
  return all;
}

function enqueue(rendered: {ts: number, level: string, src: string, msg: string}[]) {
  for(const entry of rendered) {
    retryBuffer.push(entry);
    if(entry.ts > watermark) watermark = entry.ts;
  }
  while(retryBuffer.length > RETRY_BUFFER_MAX) retryBuffer.shift();
}

let lastFlushAttempt = 0;

// Noise filter: the mtproto worker logs every ping/cleanup/queue tick (≈200
// lines/s across contexts) — more than any batch budget can carry and more
// than any reader wants. These prefixes are transport heartbeats, not signal.
const NOISE_PREFIXES = ['[sendPingDelay', '[cleanupSent', '[clearNextReq', '[scheduleReque', '[sendEncrypted | sending {"msg_id"'];
const NOISE_MSG_PATTERNS = [/"humanReadable":"(ping_delay_disconnect|pong|msgs_ack|destroy_session)"/];

function isNoise(entry: {ts: number, level: string, src: string, msg: string}): boolean {
  if(entry.level === 'error' || entry.level === 'warn') return false;
  for(const p of NOISE_PREFIXES) {
    if(entry.src.includes(p) || entry.msg.startsWith(p)) return true;
  }
  for(const p of NOISE_MSG_PATTERNS) {
    if(p.test(entry.msg)) return true;
  }
  return false;
}

async function flush(): Promise<void> {
  if(shipping) return;
  shipping = true;
  try {
    const collected = await collectSince(watermark);
    enqueue(collected.map(renderEntry).filter((e) => !isNoise(e)));
    if(retryBuffer.length === 0) return;

    const batch = retryBuffer.splice(0, MAX_BATCH);
    const body = JSON.stringify({
      build: `${import.meta.env.VITE_VERSION_FULL || 'webk'}`,
      user_id: userId,
      session_tag: sessionTag(),
      entries: batch
    });
    // A fetch that never settles (stuck connection) would hold the shipping
    // flag forever and silently stop all later flushes — the abort timeout is
    // what keeps the loop alive through network churn.
    const controller = new AbortController();
    const abortTimer = window.setTimeout(() => controller.abort(), 10_000);
    const res = await fetch(URL_BASE!, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${INTAKE_KEY}`,
        'Content-Type': 'application/json'
      },
      body,
      keepalive: true,
      signal: controller.signal
    }).finally(() => window.clearTimeout(abortTimer));
    if(!res.ok) {
      // Put the batch back (oldest-first) unless the server rejected it as
      // invalid — 4xx except 429 means "never retry this shape".
      if(res.status === 429 || res.status >= 500) {
        retryBuffer.unshift(...batch.slice(0, RETRY_BUFFER_MAX - retryBuffer.length));
      }
    }
  } catch(err) {
    // Offline / server down / abort: the ring buffer is still there; the next
    // flush picks up whatever the watermark has not consumed.
  } finally {
    shipping = false;
    lastFlushAttempt = Date.now();
  }
}

function scheduleLoop(): void {
  window.setInterval((): void => void flush(), FLUSH_INTERVAL_MS);
  // Errors should not wait a full interval: check often, ship only when
  // something at error level is pending (or the interval elapsed anyway).
  window.setInterval(() => {
    if(Date.now() - lastFlushAttempt < ERROR_FAST_FLUSH_MS) return;
    if(retryBuffer.some((e) => e.level === 'error')) void flush();
  }, ERROR_FAST_FLUSH_MS);
  document.addEventListener('visibilitychange', () => {
    if(document.visibilityState === 'hidden') void flush(); // keepalive fires while the tab dies
  });
}

function start(): void {
  if(!URL_BASE || !INTAKE_KEY) return;

  // The ring buffers default to DEBUG-only in production; shipping IS the
  // reason to record in production. Enable everywhere the flags can reach.
  setLogBufferEnabled(true);
  window.setTimeout(() => {
    try {
      apiManagerProxy.invokeVoid('setLogBufferEnabled', true);
      apiManagerProxy.serviceMessagePort?.invokeVoid('setLogBufferEnabled', true);
    } catch(err) {
      // Ports not spawned yet; the SW/worker will be re-flagged by the next
      // app boot or a debug reload — main-thread logs still ship.
    }
  }, 2_000);

  rootScope.addEventListener('user_auth', ({id}) => {
    userId = id;
  });

  // Uncaught errors do not go through logger(); route them into the buffer's
  // shipping path directly so signup crashes reach the log server even when
  // nothing else has logged yet.
  window.addEventListener('error', (event) => {
    enqueue([{
      ts: Date.now(),
      level: 'error',
      src: 'window.onerror',
      msg: `${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`
    }]);
  });
  window.addEventListener('unhandledrejection', (event) => {
    enqueue([{
      ts: Date.now(),
      level: 'error',
      src: 'unhandledrejection',
      msg: renderArg(event.reason)
    }]);
  });

  scheduleLoop();
  void flush();
}

start();
